import { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { css } from "styled-system/css";
import Markdown from "react-markdown";
import { Figure, RankedBars, Ledger } from "~/components/charts";
import { api } from "~/lib/api";
import { MoreVertical } from "lucide-react";
import { usePeriod } from "~/lib/period";
import {
  capitalize,
  countWord,
  fmtNum,
  fmtUsd,
  fmtUsdShort,
  fmtUsdWhole,
  inPeriod,
  periodName,
} from "~/lib/format";
import { ed, btn, field, tbl, AI_LINKS, Masthead } from "~/components/editorial";

// Monthly pricing constants
const COPILOT_MONTHLY_COST = 19; // $19/seat/month
const CLAUDE_MAX_MONTHLY_COST = 125; // Claude Max plan
const CLAUDE_REGULAR_MONTHLY_COST = 25; // Claude regular subscription

// Claude Code user data from API
interface ClaudeCodeUser {
  email: string;
  totalSessions: number;
  totalLinesOfCode: number;
  totalLinesAdded: number;
  totalLinesRemoved: number;
  totalCommits: number;
  totalPullRequests: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCacheReadTokens: number;
  totalCacheCreationTokens: number;
  totalCostUsd: number;
  activeDays: number;
  lastActiveDate: string | null;
  editToolAccepted: number;
  editToolRejected: number;
  writeToolAccepted: number;
  writeToolRejected: number;
  developerId: string | null;
  developerName: string | null;
  avatarUrl: string | null;
  hasClaudeCodeAccess: boolean;
  customerType: "subscription" | "api" | "mixed" | "unknown";
}

interface ClaudeCodeUsageResponse {
  success: boolean;
  users: ClaudeCodeUser[];
  totalCostUsd: number;
  totalSessions: number;
  period: { from: string; to: string };
}

// Cursor user data from API
interface CursorUser {
  cursorEmail: string;
  developerId: string | null;
  developerName: string | null;
  avatarUrl: string | null;
  githubUsername: string | null;
  totalSpendCents: number;
  totalSpendUsd: number;
  totalTabsAccepted: number;
  totalTabsSuggested: number;
  totalTabLines: number;
  totalAgentEdits: number;
  totalComposerLines: number;
  activeDays: number;
  tabAcceptanceRate: string;
}

interface CursorUsageResponse {
  success: boolean;
  users: CursorUser[];
  totals: {
    totalSpendUsd: number;
    totalUsers: number;
    totalTabsAccepted: number;
    totalTabsSuggested: number;
    totalAgentEdits: number;
    totalLinesFromAI: number;
  };
  period: { from: string; to: string };
}

// Copilot user data from API
interface CopilotUser {
  githubLogin: string;
  githubId: number;
  avatarUrl: string | null;
  planType: string | null;
  assignedAt: string;
  lastActivityAt: string | null;
  lastActivityEditor: string | null;
  organization: string;
  developerId: string | null;
  developerName: string | null;
  developerEmail: string | null;
}

interface CopilotUsageResponse {
  success: boolean;
  users: CopilotUser[];
  totals: {
    totalSeats: number;
    activeUsers: number;
    avgDailyActiveUsers: number;
    avgDailyEngagedUsers: number;
    suggestions: number;
    acceptances: number;
    linesAccepted: number;
    linesSuggested: number;
    acceptanceRate: number;
    chatTurns: number;
    prSummariesCreated: number;
  };
  period: { from: string; to: string };
}

// Combined user for display
interface CombinedAIUser {
  email: string;
  developerId: string | null;
  developerName: string | null;
  avatarUrl: string | null;
  githubLogin: string | null;
  hasClaudeCodeAccess: boolean;
  // Claude customer type from Anthropic Admin API (only set when claudeCode is present)
  claudeCustomerType: "subscription" | "api" | "mixed" | "unknown" | null;
  // Claude Code stats
  claudeCode: {
    sessions: number;
    linesOfCode: number;
    costUsd: number;
    totalTokens: number;
    activeDays: number;
  } | null;
  // Cursor stats
  cursor: {
    tabsAccepted: number;
    tabLinesAdded: number;
    agentEdits: number;
    composerLines: number;
    spendUsd: number;
    acceptanceRate: string;
    activeDays: number;
  } | null;
  // Copilot stats
  copilot: {
    lastActivityAt: string | null;
    lastActivityEditor: string | null;
    planType: string | null;
  } | null;
  // Combined totals
  totalCostUsd: number;
  totalLinesFromAI: number;
  activeDays: number;
}

// AI Overview response
interface AIOverviewResponse {
  success: boolean;
  overview: string | null;
  highSpendUsers: Array<{
    email: string;
    developerName: string | null;
    costUsd: number;
  }>;
  cached?: boolean;
}

type SortKey =
  | "name"
  | "claudeCodeCost"
  | "cursorCost"
  | "totalCost"
  | "linesFromAI"
  | "activeDays";
type SortDirection = "asc" | "desc";

// Developer type for the dropdown
interface DeveloperOption {
  id: string;
  displayName: string;
  email: string | null;
  avatarUrl: string | null;
  hasClaudeCodeAccess: boolean;
}

// User action menu component

// User action menu component
function UserActionMenu({
  user,
  onTogglePremium,
  isPending,
}: {
  user: CombinedAIUser;
  onTogglePremium: () => void;
  isPending: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className={styles.menuWrapper} ref={menuRef}>
      <button
        type="button"
        className={btn.icon}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isPending}
        aria-label={`Actions for ${user.developerName || user.email}`}
      >
        <MoreVertical size={14} />
      </button>
      {isOpen && (
        <div className={styles.menuDropdown}>
          <button
            type="button"
            className={styles.menuItem}
            onClick={() => {
              onTogglePremium();
              setIsOpen(false);
            }}
            disabled={isPending}
          >
            {user.hasClaudeCodeAccess ? "Remove Claude Max" : "Mark as Claude Max"}
          </button>
        </div>
      )}
    </div>
  );
}

// Premium users management component
function PremiumUsersManager({
  onTogglePremium,
  isPending,
}: {
  onTogglePremium: (developerId: string, hasAccess: boolean) => void;
  isPending: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch all developers
  const { data: developersData } = useQuery({
    queryKey: ["developers-for-premium"],
    queryFn: () => api.get<{ data: { items: DeveloperOption[] } }>("/developers?limit=500"),
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const allDevelopers = developersData?.data?.items || [];

  // Filter non-premium developers for adding
  const availableDevelopers = allDevelopers.filter((dev: DeveloperOption) => {
    if (dev.hasClaudeCodeAccess) return false;
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        dev.displayName.toLowerCase().includes(searchLower) ||
        dev.email?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  return (
    <div className={styles.addPremiumWrapper} ref={dropdownRef}>
      <button
        type="button"
        className={btn.secondary}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isPending}
      >
        Add a Claude Max user
      </button>
      {isOpen && (
        <div className={styles.addPremiumDropdown}>
          <input
            type="text"
            className={field.input}
            placeholder="Find a developer"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className={styles.developerList}>
            {availableDevelopers.length === 0 ? (
              <div className={styles.noResults}>
                {search ? "No developers found" : "Everyone is already on Claude Max"}
              </div>
            ) : (
              availableDevelopers.slice(0, 10).map((dev: DeveloperOption) => (
                <button
                  type="button"
                  key={dev.id}
                  className={styles.developerItem}
                  onClick={() => {
                    onTogglePremium(dev.id, true);
                    setIsOpen(false);
                    setSearch("");
                  }}
                  disabled={isPending}
                >
                  <span className={styles.devName}>{dev.displayName}</span>
                  {dev.email && <span className={styles.devEmail}>{dev.email}</span>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Which tools a person has, as short text: ink when present. */
function toolNames(user: CombinedAIUser): Array<{ label: string; title: string }> {
  const out: Array<{ label: string; title: string }> = [];
  if (user.claudeCode || user.hasClaudeCodeAccess) {
    out.push({
      label: user.hasClaudeCodeAccess ? "Claude Max" : "Claude Code",
      title: user.hasClaudeCodeAccess
        ? user.claudeCode
          ? "Claude Code (Max)"
          : "Claude Max (no usage yet)"
        : user.claudeCustomerType === "subscription" || user.claudeCustomerType === "mixed"
          ? "Claude Code (regular subscription)"
          : "Claude Code (API)",
    });
  }
  if (user.cursor) out.push({ label: "Cursor", title: "Cursor" });
  if (user.copilot)
    out.push({
      label: "Copilot",
      title: `Copilot${user.copilot.lastActivityEditor ? ` (${user.copilot.lastActivityEditor})` : ""}`,
    });
  return out;
}

export function AIUsagePage() {
  const { range, window: periodWin } = usePeriod();
  const [sortKey, setSortKey] = useState<SortKey>("totalCost");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");
  const queryClient = useQueryClient();

  // Fetch Claude Code usage
  const { data: claudeCodeData, isLoading: claudeCodeLoading } = useQuery({
    queryKey: ["claude-code-usage", range],
    queryFn: () =>
      api.get<ClaudeCodeUsageResponse>(`/claude-code/usage?range=${range}`),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch Cursor usage
  const { data: cursorData, isLoading: cursorLoading } = useQuery({
    queryKey: ["cursor-usage", range],
    queryFn: () => api.get<CursorUsageResponse>(`/cursor/usage?range=${range}`),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch Copilot usage
  const { data: copilotData, isLoading: copilotLoading } = useQuery({
    queryKey: ["copilot-usage", range],
    queryFn: () =>
      api.get<CopilotUsageResponse>(`/copilot/usage?range=${range}`),
    staleTime: 5 * 60 * 1000,
  });

  // AI-generated overview - check cache on load, generate on user action
  const [generateOverview, setGenerateOverview] = useState(false);

  // Check for cached overview on page load
  const { data: cachedOverviewData } = useQuery({
    queryKey: ["ai-usage-overview-cached", range],
    queryFn: () =>
      api.get<AIOverviewResponse>(
        `/ai-usage/overview?range=${range}&cached_only=true`,
      ),
    staleTime: 10 * 60 * 1000,
  });

  // Generate new overview when user clicks button
  const { data: generatedOverviewData, isLoading: overviewLoading } = useQuery({
    queryKey: ["ai-usage-overview", range],
    queryFn: () =>
      api.get<AIOverviewResponse>(`/ai-usage/overview?range=${range}`),
    staleTime: 10 * 60 * 1000,
    enabled: generateOverview,
  });

  // Use cached data if available, otherwise use generated data
  const overviewData =
    generatedOverviewData ||
    (cachedOverviewData?.overview ? cachedOverviewData : null);
  const hasCachedOverview =
    cachedOverviewData?.overview && cachedOverviewData?.cached;

  // Fetch all developers to include premium users without usage
  const { data: developersData } = useQuery({
    queryKey: ["developers-for-premium"],
    queryFn: () =>
      api.get<{
        data: {
          items: Array<{
            id: string;
            displayName: string;
            email: string | null;
            avatarUrl: string | null;
            hasClaudeCodeAccess: boolean;
          }>;
        };
      }>("/developers?limit=500"),
    staleTime: 5 * 60 * 1000,
  });

  // Mutation for toggling Claude Premium status
  const togglePremiumMutation = useMutation({
    mutationFn: async ({
      developerId,
      hasAccess,
    }: {
      developerId: string;
      hasAccess: boolean;
    }) => {
      console.log("Toggling premium:", { developerId, hasAccess });
      return api.patch(`/developers/${developerId}/claude-code-access`, {
        hasAccess,
      });
    },
    onSuccess: () => {
      console.log("Premium toggle successful, invalidating queries");
      // Invalidate queries to refetch with updated premium status
      queryClient.invalidateQueries({ queryKey: ["claude-code-usage"] });
      queryClient.invalidateQueries({ queryKey: ["developers-for-premium"] });
    },
    onError: (error) => {
      console.error("Failed to toggle premium status:", error);
      alert(`Failed to update premium status: ${error.message}`);
    },
  });

  const isLoading = claudeCodeLoading || cursorLoading || copilotLoading;

  // Combine Claude Code and Cursor users into a single list
  const combinedUsers = useMemo(() => {
    const userMap = new Map<string, CombinedAIUser>();

    // Process Claude Code users
    if (claudeCodeData?.users) {
      for (const user of claudeCodeData.users) {
        const key = user.email.toLowerCase();
        userMap.set(key, {
          email: user.email,
          developerId: user.developerId,
          developerName: user.developerName,
          avatarUrl: user.avatarUrl,
          githubLogin: null,
          hasClaudeCodeAccess: user.hasClaudeCodeAccess,
          claudeCustomerType: user.customerType,
          claudeCode: {
            sessions: user.totalSessions,
            linesOfCode: user.totalLinesOfCode,
            costUsd: user.totalCostUsd,
            totalTokens: user.totalInputTokens + user.totalOutputTokens,
            activeDays: user.activeDays,
          },
          cursor: null,
          copilot: null,
          totalCostUsd: user.totalCostUsd,
          totalLinesFromAI: user.totalLinesOfCode,
          activeDays: user.activeDays,
        });
      }
    }

    // Build a map of developer IDs to premium status from developers data
    const premiumDevIds = new Set(
      (developersData?.data?.items || [])
        .filter((d) => d.hasClaudeCodeAccess)
        .map((d) => d.id),
    );

    // Process Cursor users (merge with existing or create new)
    const cursorUsers = cursorData?.users || [];
    for (const user of cursorUsers) {
      const key = user.cursorEmail.toLowerCase();
      const existing = userMap.get(key);

      if (existing) {
        // Merge with existing Claude Code user
        existing.cursor = {
          tabsAccepted: user.totalTabsAccepted,
          tabLinesAdded: user.totalTabLines,
          agentEdits: user.totalAgentEdits,
          composerLines: user.totalComposerLines,
          spendUsd: user.totalSpendUsd,
          acceptanceRate: user.tabAcceptanceRate,
          activeDays: user.activeDays,
        };
        existing.totalCostUsd += user.totalSpendUsd;
        existing.totalLinesFromAI +=
          user.totalTabLines + user.totalComposerLines;
        existing.activeDays = Math.max(existing.activeDays, user.activeDays);
        // Use Cursor's developer info if Claude Code didn't have it
        if (!existing.developerId && user.developerId) {
          existing.developerId = user.developerId;
          existing.developerName = user.developerName;
          existing.avatarUrl = user.avatarUrl;
        }
      } else {
        // New user from Cursor only - check if they're premium via developer data
        const isPremium = user.developerId
          ? premiumDevIds.has(user.developerId)
          : false;
        userMap.set(key, {
          email: user.cursorEmail,
          developerId: user.developerId,
          developerName: user.developerName,
          avatarUrl: user.avatarUrl,
          githubLogin: user.githubUsername,
          hasClaudeCodeAccess: isPremium,
          claudeCustomerType: null,
          claudeCode: null,
          cursor: {
            tabsAccepted: user.totalTabsAccepted,
            tabLinesAdded: user.totalTabLines,
            agentEdits: user.totalAgentEdits,
            composerLines: user.totalComposerLines,
            spendUsd: user.totalSpendUsd,
            acceptanceRate: user.tabAcceptanceRate,
            activeDays: user.activeDays,
          },
          copilot: null,
          totalCostUsd: user.totalSpendUsd,
          totalLinesFromAI: user.totalTabLines + user.totalComposerLines,
          activeDays: user.activeDays,
        });
      }
    }

    // Add premium developers who don't have any usage data
    const premiumDevs =
      developersData?.data?.items?.filter((d) => d.hasClaudeCodeAccess) || [];
    for (const dev of premiumDevs) {
      // Check if already in the map (by developer ID or email)
      const existingByEmail = dev.email
        ? userMap.get(dev.email.toLowerCase())
        : null;
      const existingById = Array.from(userMap.values()).find(
        (u) => u.developerId === dev.id,
      );

      if (!existingByEmail && !existingById) {
        // Add premium developer without usage data
        const key = dev.email?.toLowerCase() || dev.id;
        userMap.set(key, {
          email: dev.email || dev.displayName,
          developerId: dev.id,
          developerName: dev.displayName,
          avatarUrl: dev.avatarUrl,
          githubLogin: null,
          hasClaudeCodeAccess: true,
          claudeCustomerType: null,
          claudeCode: null,
          cursor: null,
          copilot: null,
          totalCostUsd: 0,
          totalLinesFromAI: 0,
          activeDays: 0,
        });
      }
    }

    // Process Copilot users (merge by email or GitHub login)
    const copilotUsers = copilotData?.users || [];
    for (const user of copilotUsers) {
      // Try to find existing user by developer email or GitHub login
      let existing: CombinedAIUser | undefined;
      let key: string;

      // First try to match by developer email
      if (user.developerEmail) {
        const emailKey = user.developerEmail.toLowerCase();
        existing = userMap.get(emailKey);
        key = emailKey;
      }

      // If not found by email, try to find by developerId
      if (!existing && user.developerId) {
        existing = Array.from(userMap.values()).find(
          (u) => u.developerId === user.developerId,
        );
        key =
          user.developerEmail?.toLowerCase() || user.githubLogin.toLowerCase();
      }

      // Use GitHub login as fallback key
      if (!key!) {
        key = user.githubLogin.toLowerCase();
      }

      if (existing) {
        // Merge with existing user
        existing.copilot = {
          lastActivityAt: user.lastActivityAt,
          lastActivityEditor: user.lastActivityEditor,
          planType: user.planType,
        };
        existing.githubLogin = user.githubLogin;
        // Use Copilot's avatar if not set
        if (!existing.avatarUrl && user.avatarUrl) {
          existing.avatarUrl = user.avatarUrl;
        }
        // Use Copilot's developer info if not set
        if (!existing.developerId && user.developerId) {
          existing.developerId = user.developerId;
          existing.developerName = user.developerName;
        }
      } else {
        // New user from Copilot only
        const isPremium = user.developerId
          ? premiumDevIds.has(user.developerId)
          : false;
        userMap.set(key, {
          email: user.developerEmail || user.githubLogin,
          developerId: user.developerId,
          developerName: user.developerName,
          avatarUrl: user.avatarUrl,
          githubLogin: user.githubLogin,
          hasClaudeCodeAccess: isPremium,
          claudeCustomerType: null,
          claudeCode: null,
          cursor: null,
          copilot: {
            lastActivityAt: user.lastActivityAt,
            lastActivityEditor: user.lastActivityEditor,
            planType: user.planType,
          },
          totalCostUsd: COPILOT_MONTHLY_COST, // Copilot seat cost
          totalLinesFromAI: 0,
          activeDays: 0,
        });
      }
    }

    // Add Copilot $19 cost to existing users with Copilot seats
    for (const user of userMap.values()) {
      if (user.copilot) {
        // Add $19 for Copilot seat to users that were merged
        // (new Copilot-only users already have it set above)
        if (user.claudeCode || user.cursor) {
          user.totalCostUsd += COPILOT_MONTHLY_COST;
        }
      }
    }

    return Array.from(userMap.values());
  }, [claudeCodeData, cursorData, copilotData, developersData]);

  // Sort users - premium users always appear first
  const sortedUsers = useMemo(() => {
    return [...combinedUsers].sort((a, b) => {
      // Premium users always come first
      if (a.hasClaudeCodeAccess !== b.hasClaudeCodeAccess) {
        return a.hasClaudeCodeAccess ? -1 : 1;
      }

      let comparison = 0;
      switch (sortKey) {
        case "name":
          comparison = (a.developerName || a.email).localeCompare(
            b.developerName || b.email,
          );
          break;
        case "claudeCodeCost":
          comparison =
            (a.claudeCode?.costUsd || 0) - (b.claudeCode?.costUsd || 0);
          break;
        case "cursorCost":
          comparison = (a.cursor?.spendUsd || 0) - (b.cursor?.spendUsd || 0);
          break;
        case "totalCost":
          comparison = a.totalCostUsd - b.totalCostUsd;
          break;
        case "linesFromAI":
          comparison = a.totalLinesFromAI - b.totalLinesFromAI;
          break;
        case "activeDays":
          comparison = a.activeDays - b.activeDays;
          break;
      }
      return sortDir === "desc" ? -comparison : comparison;
    });
  }, [combinedUsers, sortKey, sortDir]);

  // Calculate totals
  const totals = useMemo(() => {
    const claudeCodeTotal = claudeCodeData?.totalCostUsd || 0;
    const cursorTotal = cursorData?.totals?.totalSpendUsd || 0;
    const claudeCodeUsers = claudeCodeData?.users?.length || 0;
    const cursorUsersCount = cursorData?.users?.length || 0;
    const copilotSeats = copilotData?.totals?.totalSeats || 0;
    const copilotActiveUsers = copilotData?.totals?.activeUsers || 0;
    const copilotLinesAccepted = copilotData?.totals?.linesAccepted || 0;
    const copilotAcceptanceRate = copilotData?.totals?.acceptanceRate || 0;
    const uniqueUsers = combinedUsers.length;
    const totalLines =
      combinedUsers.reduce((sum, u) => sum + u.totalLinesFromAI, 0) +
      copilotLinesAccepted;

    // Pricing calculations
    const copilotCost = copilotSeats * COPILOT_MONTHLY_COST;
    const maxUsers = combinedUsers.filter((u) => u.hasClaudeCodeAccess).length;
    // Regular = subscription customer on the Anthropic side, but not marked Max
    const regularUsers = combinedUsers.filter(
      (u) =>
        !u.hasClaudeCodeAccess &&
        (u.claudeCustomerType === "subscription" ||
          u.claudeCustomerType === "mixed"),
    ).length;
    const claudeMaxCost = maxUsers * CLAUDE_MAX_MONTHLY_COST;
    const claudeRegularCost = regularUsers * CLAUDE_REGULAR_MONTHLY_COST;
    const claudeSubscriptionCost = claudeMaxCost + claudeRegularCost;
    const totalCost =
      claudeCodeTotal + cursorTotal + copilotCost + claudeSubscriptionCost;

    return {
      claudeCodeCost: claudeCodeTotal,
      cursorCost: cursorTotal,
      copilotCost,
      claudeMaxCost,
      claudeRegularCost,
      claudeSubscriptionCost,
      totalCost,
      claudeCodeUsers,
      cursorUsers: cursorUsersCount,
      copilotSeats,
      copilotActiveUsers,
      copilotLinesAccepted,
      copilotAcceptanceRate,
      maxUsers,
      regularUsers,
      uniqueUsers,
      totalLines,
    };
  }, [claudeCodeData, cursorData, copilotData, combinedUsers]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  // Calculate loading progress
  const loadingProgress = useMemo(() => {
    const sources = [
      { name: "Claude Code", loaded: !claudeCodeLoading },
      { name: "Cursor", loaded: !cursorLoading },
      { name: "Copilot", loaded: !copilotLoading },
    ];
    const loadedCount = sources.filter((s) => s.loaded).length;
    return {
      sources,
      loadedCount,
      total: sources.length,
      percent: (loadedCount / sources.length) * 100,
    };
  }, [claudeCodeLoading, cursorLoading, copilotLoading]);


  const now = periodName(periodWin.label);
  const sortHead = (key: SortKey, label: string, num = true) => (
    <th
      data-num={num || undefined}
      data-active={sortKey === key || undefined}
      aria-sort={sortKey === key ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button type="button" onClick={() => handleSort(key)}>
        {label}
        {sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
      </button>
    </th>
  );

  if (isLoading) {
    return (
      <div className={ed.page}>
        <div className={ed.inner}>
          <Masthead links={AI_LINKS} meta={periodWin.label} />
          <div className={ed.loading}>
            Loading AI usage, {loadingProgress.loadedCount} of {loadingProgress.total} sources:{" "}
            {loadingProgress.sources.map((s, i) => (
              <span key={s.name} className={s.loaded ? undefined : ed.muted}>
                {i > 0 ? " · " : ""}
                {s.loaded ? "✓" : "○"} {s.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // People whose usage-billed Claude Code spend passed the Max seat price
  const overMax = combinedUsers.filter(
    (u) => !u.hasClaudeCodeAccess && (u.claudeCode?.costUsd ?? 0) >= CLAUDE_MAX_MONTHLY_COST,
  );
  const overMaxIds = new Set(overMax.map((u) => u.email));
  const bySpend = [...combinedUsers]
    .filter((u) => u.totalCostUsd > 0)
    .sort((a, b) => b.totalCostUsd - a.totalCostUsd);
  const shownSpend = bySpend.slice(0, FIGURE_ROWS);
  const lines = [
    { id: "cc", label: "Claude Code", value: totals.claudeCodeCost },
    { id: "subs", label: "Claude seats", value: totals.claudeSubscriptionCost },
    { id: "cursor", label: "Cursor", value: totals.cursorCost },
    { id: "copilot", label: "Copilot seats", value: totals.copilotCost },
  ].sort((a, b) => b.value - a.value);
  const lead = lines[0];

  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <Masthead links={AI_LINKS} meta={periodWin.label} />

        <div className={ed.kicker}>AI tools · {now}</div>
        <h1 className={ed.headline}>
          {totals.uniqueUsers === 0
            ? `No AI tool usage recorded ${inPeriod(periodWin.label)}`
            : `AI tools cost ${fmtUsd(totals.totalCost)} ${inPeriod(periodWin.label)}, across ${fmtNum(totals.uniqueUsers)} ${totals.uniqueUsers === 1 ? "person" : "people"}`}
        </h1>
        <p className={ed.dek}>
          Claude Code usage came to {fmtUsd(totals.claudeCodeCost)} and Cursor to{" "}
          {fmtUsd(totals.cursorCost)}. Copilot is {fmtNum(totals.copilotSeats)}{" "}
          {totals.copilotSeats === 1 ? "seat" : "seats"} at ${COPILOT_MONTHLY_COST} a month, and
          Claude subscriptions add {fmtUsd(totals.claudeSubscriptionCost)} ({fmtNum(totals.maxUsers)}{" "}
          Max, {fmtNum(totals.regularUsers)} regular). The tools wrote{" "}
          {fmtNum(totals.totalLines)} lines.
          {overMax.length > 0 &&
            ` ${capitalize(countWord(overMax.length))} ${overMax.length === 1 ? "person" : "people"} on usage billing spent more on Claude Code than a Max seat costs.`}
        </p>
        <div className={ed.bylineSplit}>
          <span>
            <b>AI spend</b> · usage from the Anthropic and Cursor admin APIs, Copilot seats from
            GitHub. Seat prices: Copilot ${COPILOT_MONTHLY_COST}, Claude Max $
            {CLAUDE_MAX_MONTHLY_COST}, Claude regular ${CLAUDE_REGULAR_MONTHLY_COST}, per month.
          </span>
          <Link to="/ai-impact">What it produced →</Link>
        </div>

        <Ledger
          hideMissingPrior
          items={[
            { label: "Total AI spend", value: fmtUsdWhole(totals.totalCost), note: `usage + ${fmtNum(totals.copilotSeats)} Copilot seats` },
            {
              label: "Claude subscriptions",
              value: fmtUsdWhole(totals.claudeSubscriptionCost),
              note: `${totals.maxUsers} Max, ${totals.regularUsers} regular`,
            },
            { label: "Claude Code usage", value: fmtUsdWhole(totals.claudeCodeCost), note: `${fmtNum(totals.claudeCodeUsers)} people, per token` },
            { label: "Cursor usage", value: fmtUsdWhole(totals.cursorCost), note: `${fmtNum(totals.cursorUsers)} people` },
            { label: "Copilot", value: fmtUsdWhole(totals.copilotCost), note: `${totals.copilotSeats} seats, ${totals.copilotActiveUsers} active` },
            { label: "Lines from AI", value: fmtNum(totals.totalLines), note: "all three tools" },
          ]}
        />

        <div className={ed.sect}>I. Where the money went</div>
        <h2 className={ed.h2}>
          {overMax.length > 0
            ? `${capitalize(countWord(overMax.length))} ${overMax.length === 1 ? "person" : "people"} on usage billing passed the price of a Claude Max seat`
            : lead && lead.value > 0
              ? `${lead.label} was the largest cost, at ${fmtUsd(lead.value)}`
              : "No AI spend recorded"}
        </h2>
        <div className={ed.twoUp}>
          {shownSpend.length > 0 ? (
            <Figure
              title="Spend per person"
              sub={`People whose Claude Code usage passed $${CLAUDE_MAX_MONTHLY_COST}, the Max seat price, in color.`}
              method={`Claude Code and Cursor usage in the period, plus $${COPILOT_MONTHLY_COST} for a Copilot seat. Claude subscription fees are not split per person.${bySpend.length > shownSpend.length ? ` Showing ${shownSpend.length} of ${bySpend.length}; the table has all.` : ""}`}
              table={{
                columns: ["Person", "Claude Code", "Cursor", "Total"],
                rows: bySpend.map((u) => [
                  u.developerName || u.email,
                  fmtUsd(u.claudeCode?.costUsd ?? 0),
                  fmtUsd(u.cursor?.spendUsd ?? 0),
                  fmtUsd(u.totalCostUsd),
                ]),
              }}
            >
              <RankedBars
                ariaLabel="AI spend per person"
                format={fmtUsdShort}
                rows={shownSpend.map((u) => ({
                  id: u.email,
                  label: u.developerName || u.email,
                  value: u.totalCostUsd,
                  emphasis: overMaxIds.has(u.email),
                }))}
              />
            </Figure>
          ) : (
            <p className={ed.prose}>No per-person spend in this period.</p>
          )}
          <Figure
            title="Spend by line item"
            sub="The largest line in color."
            method="Usage lines are billed amounts for the period. Seat lines are monthly list price times seats."
          >
            <RankedBars
              ariaLabel="AI spend by line item"
              width={420}
              format={fmtUsdShort}
              rows={lines.map((l, i) => ({ ...l, emphasis: i === 0 && l.value > 0 }))}
            />
          </Figure>
        </div>

        <div className={ed.sect}>II. Summary notes</div>
        {hasCachedOverview || generateOverview ? (
          <>
            <p className={ed.methodMono} style={{ marginTop: 6, marginBottom: 10 }}>
              Written by a language model from the usage above. Check before quoting.
            </p>
            {overviewLoading ? (
              <div className={ed.loading} style={{ paddingTop: 12, paddingBottom: 12 }}>
                Writing the summary…
              </div>
            ) : overviewData?.overview ? (
              <>
                <div className={ed.prose}>
                  <Markdown>{overviewData.overview}</Markdown>
                </div>
                {overviewData.highSpendUsers && overviewData.highSpendUsers.length > 0 && (
                  <div className={ed.notice}>
                    <b>
                      {overviewData.highSpendUsers.length}{" "}
                      {overviewData.highSpendUsers.length > 1 ? "people" : "person"} near the Claude
                      Max price (${CLAUDE_MAX_MONTHLY_COST}):
                    </b>{" "}
                    {overviewData.highSpendUsers.map((u) => u.developerName || u.email).join(", ")}
                  </div>
                )}
              </>
            ) : null}
          </>
        ) : (
          <div className={styles.promptRow}>
            <p className={ed.small} style={{ marginBottom: 0 }}>
              A language model can write a short summary of usage patterns from these numbers.
            </p>
            <button type="button" className={btn.primary} onClick={() => setGenerateOverview(true)}>
              Write summary
            </button>
          </div>
        )}

        <div className={ed.sect}>III. Per person</div>
        <h2 className={ed.h2}>Everyone with an AI tool in {now}</h2>
        <p className={ed.prose}>
          Claude Max users first, then by the column you sort. Small figures under a cost are
          sessions for Claude Code and tab acceptance for Cursor.
        </p>
        {sortedUsers.length === 0 ? (
          <p className={ed.small}>
            No AI usage found. Connect Claude Code or Cursor in Admin to start tracking it.
          </p>
        ) : (
          <div className={tbl.scroll}>
            <table className={tbl.table}>
              <thead>
                <tr>
                  {sortHead("name", "Person", false)}
                  <th>Tools</th>
                  {sortHead("claudeCodeCost", "Claude Code")}
                  {sortHead("cursorCost", "Cursor")}
                  {sortHead("totalCost", "Total")}
                  {sortHead("linesFromAI", "Lines")}
                  {sortHead("activeDays", "Active days")}
                  <th aria-hidden />
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((user) => (
                  <tr key={user.email}>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {user.developerId ? (
                        <Link to={`/developers/${user.developerId}`}>
                          {user.developerName || user.email}
                        </Link>
                      ) : (
                        <span style={{ fontWeight: 600 }}>{user.developerName || user.email}</span>
                      )}
                      <span className={tbl.subBlock}>{user.email}</span>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {toolNames(user).map((t, i) => (
                        <span key={t.label} title={t.title}>
                          {i > 0 ? ", " : ""}
                          {t.label === "Claude Max" ? <b>{t.label}</b> : t.label}
                        </span>
                      ))}
                    </td>
                    <td data-num data-flag={overMaxIds.has(user.email) || undefined}>
                      {user.claudeCode ? (
                        <>
                          {fmtUsd(user.claudeCode.costUsd)}
                          <span className={tbl.subBlock}>{fmtNum(user.claudeCode.sessions)} sessions</span>
                        </>
                      ) : (
                        <span className={ed.muted}>–</span>
                      )}
                    </td>
                    <td data-num>
                      {user.cursor ? (
                        <>
                          {fmtUsd(user.cursor.spendUsd)}
                          <span className={tbl.subBlock}>{user.cursor.acceptanceRate}% accepted</span>
                        </>
                      ) : (
                        <span className={ed.muted}>–</span>
                      )}
                    </td>
                    <td data-num style={{ fontWeight: 600 }}>
                      {fmtUsd(user.totalCostUsd)}
                    </td>
                    <td data-num>
                      <span className={user.totalLinesFromAI ? undefined : ed.muted}>
                        {fmtNum(user.totalLinesFromAI)}
                      </span>
                    </td>
                    <td data-num>
                      <span className={user.activeDays ? undefined : ed.muted}>{user.activeDays}</span>
                    </td>
                    <td>
                      {user.developerId && (
                        <UserActionMenu
                          user={user}
                          onTogglePremium={() =>
                            togglePremiumMutation.mutate({
                              developerId: user.developerId!,
                              hasAccess: !user.hasClaudeCodeAccess,
                            })
                          }
                          isPending={togglePremiumMutation.isPending}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className={ed.methodMono}>
          Shaded Claude Code cells: usage billing above the ${CLAUDE_MAX_MONTHLY_COST} Max seat
          price. Total adds ${COPILOT_MONTHLY_COST} for a Copilot seat. Lines: Claude Code lines of
          code, Cursor tab and composer lines.
        </p>

        <div className={ed.sect}>IV. Claude Max seats</div>
        <h2 className={ed.h2}>
          {fmtNum(totals.maxUsers)} {totals.maxUsers === 1 ? "person is" : "people are"} marked as
          Claude Max
        </h2>
        <p className={ed.small}>
          Anthropic's Admin API doesn't say whether a subscriber is on Pro or Max, so Max is marked
          by hand. Anyone with Claude Code activity on a subscription is counted as a regular ($
          {CLAUDE_REGULAR_MONTHLY_COST}/mo) subscriber by default; mark someone here to count them
          at the Max rate (${CLAUDE_MAX_MONTHLY_COST}/mo). Use the menu at the end of a row to
          remove someone.
        </p>
        <PremiumUsersManager
          onTogglePremium={(developerId, hasAccess) =>
            togglePremiumMutation.mutate({ developerId, hasAccess })
          }
          isPending={togglePremiumMutation.isPending}
        />
      </div>
    </div>
  );
}

const FIGURE_ROWS = 24;

const styles = {
  promptRow: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px 24px",
    mt: "10px",
    maxWidth: "760px",
  }),
  menuWrapper: css({ position: "relative", display: "inline-block" }),
  menuDropdown: css({
    position: "absolute",
    right: 0,
    top: "100%",
    mt: "4px",
    bg: "ed.paper",
    border: "1px solid",
    borderColor: "ed.ink",
    borderRadius: 0,
    py: "4px",
    minWidth: "170px",
    whiteSpace: "nowrap",
    zIndex: 50,
  }),
  menuItem: css({
    display: "block",
    width: "100%",
    px: "10px",
    py: "6px",
    fontFamily: "sans",
    fontSize: "12.5px",
    color: "ed.ink",
    bg: "transparent",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    _hover: { bg: "ed.panel" },
    _disabled: { opacity: 0.5, cursor: "not-allowed" },
  }),
  addPremiumWrapper: css({ position: "relative", display: "inline-block", mt: "4px" }),
  addPremiumDropdown: css({
    position: "absolute",
    left: 0,
    top: "100%",
    mt: "4px",
    width: "320px",
    bg: "ed.paper",
    border: "1px solid",
    borderColor: "ed.ink",
    borderRadius: 0,
    p: "8px",
    zIndex: 50,
  }),
  developerList: css({
    display: "flex",
    flexDirection: "column",
    maxHeight: "280px",
    overflowY: "auto",
    mt: "6px",
  }),
  developerItem: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
    px: "6px",
    py: "5px",
    bg: "transparent",
    border: "none",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.grid",
    cursor: "pointer",
    textAlign: "left",
    _hover: { bg: "ed.panel" },
    _disabled: { opacity: 0.5, cursor: "not-allowed" },
  }),
  devName: css({ fontFamily: "sans", fontSize: "13px", fontWeight: 600, color: "ed.ink" }),
  devEmail: css({ fontFamily: "sans", fontSize: "11px", color: "ed.ink3" }),
  noResults: css({ fontFamily: "sans", fontSize: "12.5px", color: "ed.ink3", p: "8px" }),
};
